import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Radio, Row, Space, Switch, Typography } from 'antd';
import { api } from '../lib/api';
import AnalyticsFilters from '../components/AnalyticsFilters';
import type { AnalyticsEvent } from '../types';

const { Title, Text } = Typography;

type CountItem = { label: string; count: number };
type DailyItem = { label: string; count: number; date: string };

function formatFileSize(kb: number) {
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatTimestamp(timestamp?: string | null) {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString();
}

function getLocalDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sortCounts(map: Record<string, number>, limit: number) {
  return Object.entries(map)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

function buildBands(bands: Record<string, number>) {
  return Object.entries(bands).map(([label, count]) => ({ label, count }));
}

function maxCount(items: CountItem[]) {
  return Math.max(...items.map((item) => item.count), 1);
}

function analyzeEvents(events: AnalyticsEvent[]) {
  if (!Array.isArray(events) || events.length === 0) return null;

  const versionCount: Record<string, number> = {};
  const osCount: Record<string, number> = {};
  const browserCount: Record<string, number> = {};
  const fileCount: Record<string, number> = {};

  const featureCountBands: Record<string, number> = {
    '0-10': 0,
    '11-100': 0,
    '101-1000': 0,
    '1001-10000': 0,
    '10001-100000': 0,
    'Over 100000': 0,
  };

  const durationBands: Record<string, number> = {
    '< 0.5s': 0,
    '0.5-1s': 0,
    '1-3s': 0,
    '3-5s': 0,
    '5-10s': 0,
    '> 10s': 0,
  };

  const sizeBands: Record<string, number> = {
    '0-10 KB': 0,
    '10-100 KB': 0,
    '100 KB-1 MB': 0,
    '1-10 MB': 0,
    '10-100 MB': 0,
    '> 100 MB': 0,
  };

  let totalSize = 0;
  let totalDuration = 0;
  let totalFeatures = 0;
  let maxSize = 0;
  let maxDuration = 0;
  let maxFeatures = 0;

  events.forEach((event) => {
    const params = (event.specific || {}) as Record<string, any>;

    const version = params.aixm_version || 'Unknown';
    versionCount[version] = (versionCount[version] || 0) + 1;

    const os = event.os || params.os || 'Unknown';
    osCount[os] = (osCount[os] || 0) + 1;

    const browser = event.browser || params.browser || 'Unknown';
    browserCount[browser] = (browserCount[browser] || 0) + 1;

    const fileName = params.file_name || 'Unknown';
    fileCount[fileName] = (fileCount[fileName] || 0) + 1;

    const sizeKb = params.dataset_size_kb || 0;
    const duration = params.indexing_duration_seconds || 0;
    const features = params.total_features || 0;

    totalSize += sizeKb;
    totalDuration += duration;
    totalFeatures += features;
    maxSize = Math.max(maxSize, sizeKb);
    maxDuration = Math.max(maxDuration, duration);
    maxFeatures = Math.max(maxFeatures, features);

    if (features <= 10) featureCountBands['0-10']++;
    else if (features <= 100) featureCountBands['11-100']++;
    else if (features <= 1000) featureCountBands['101-1000']++;
    else if (features <= 10000) featureCountBands['1001-10000']++;
    else if (features <= 100000) featureCountBands['10001-100000']++;
    else featureCountBands['Over 100000']++;

    if (duration < 0.5) durationBands['< 0.5s']++;
    else if (duration < 1) durationBands['0.5-1s']++;
    else if (duration < 3) durationBands['1-3s']++;
    else if (duration < 5) durationBands['3-5s']++;
    else if (duration < 10) durationBands['5-10s']++;
    else durationBands['> 10s']++;

    if (sizeKb < 10) sizeBands['0-10 KB']++;
    else if (sizeKb < 100) sizeBands['10-100 KB']++;
    else if (sizeKb < 1024) sizeBands['100 KB-1 MB']++;
    else if (sizeKb < 10240) sizeBands['1-10 MB']++;
    else if (sizeKb < 102400) sizeBands['10-100 MB']++;
    else sizeBands['> 100 MB']++;
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = getLocalDateString(today);
  const dailyCount: Record<string, number> = {};

  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dailyCount[getLocalDateString(date)] = 0;
  }

  events.forEach((event) => {
    const timestamp = event.occurredAt || event.createdAt;
    if (!timestamp) return;
    const date = new Date(timestamp);
    const dateStr = getLocalDateString(date);
    if (dailyCount[dateStr] !== undefined) {
      dailyCount[dateStr] += 1;
    }
  });

  const daily: DailyItem[] = Object.entries(dailyCount)
    .map(([date, count]) => {
      const d = new Date(`${date}T00:00:00`);
      let label = `${d.getMonth() + 1}/${d.getDate()}`;
      if (date === todayStr) {
        label = 'Today';
      }
      return { label, count, date };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  const recent = [...events]
    .sort((a, b) => {
      const timeA = new Date(a.occurredAt || a.createdAt || 0).getTime();
      const timeB = new Date(b.occurredAt || b.createdAt || 0).getTime();
      return timeB - timeA;
    })
    .slice(0, 20);

  return {
    total: events.length,
    avgSize: totalSize / events.length,
    avgDuration: totalDuration / events.length,
    avgFeatures: totalFeatures / events.length,
    totalSize,
    totalDuration,
    totalFeatures,
    maxSize,
    maxDuration,
    maxFeatures,
    versions: sortCounts(versionCount, 5),
    os: sortCounts(osCount, 5),
    browsers: sortCounts(browserCount, 5),
    files: sortCounts(fileCount, 10),
    featureCountBands: buildBands(featureCountBands),
    durationBands: buildBands(durationBands),
    sizeBands: buildBands(sizeBands),
    daily,
    recent,
  };
}

function BarList({ title, items }: { title: string; items: CountItem[] }) {
  const max = maxCount(items);
  return (
    <Card style={{ height: '100%' }}>
      <Title level={4}>{title}</Title>
      {items.length === 0 ? (
        <Text type="secondary">No data</Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((item) => {
            const width = `${(item.count / max) * 100}%`;
            return (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ minWidth: 110, color: '#555' }}>{item.label}</div>
                <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                  <div
                    style={{
                      width,
                      background: 'linear-gradient(90deg, #f46524 0%, #f08f4b 100%)',
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 600,
                      paddingRight: 8,
                    }}
                  >
                    {item.count}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function ColumnChart({ title, items }: { title: string; items: DailyItem[] }) {
  const max = Math.max(...items.map((item) => item.count), 1);
  return (
    <Card>
      <Title level={4}>{title}</Title>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200 }}>
        {items.map((item) => {
          const height = Math.max((item.count / max) * 180, 2);
          return (
            <div key={item.date} style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div
                style={{
                  height,
                  background: 'linear-gradient(180deg, #f46524 0%, #f08f4b 100%)',
                  borderRadius: '4px 4px 0 0',
                  position: 'relative',
                }}
              >
                {item.count > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -18,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#f46524',
                    }}
                  >
                    {item.count}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: '#666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function AnalyticsAixmPage() {
  const [viewerType, setViewerType] = useState<'light' | 'pro'>('light');
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [ignoreAnnea, setIgnoreAnnea] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const selectedAppSlugs = viewerType === 'pro'
    ? ['aixm-viewer-pro', 'aixmviewerpro']
    : ['aixm-viewer-light']; // The API includes the former aixm-viewer slug.

  useEffect(() => {
    loadEvents();
  }, [viewerType, ignoreAnnea, selectedOrgId, selectedUserId]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = window.setInterval(() => {
      loadEvents();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, viewerType, ignoreAnnea, selectedOrgId, selectedUserId]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const responses = await Promise.allSettled(
        selectedAppSlugs.map((appSlug) =>
          api.analytics.events(appSlug, {
            days: 90,
            limit: 5000,
            eventName: 'dataset_opened',
            organizationId: selectedOrgId || undefined,
            userId: selectedUserId || undefined,
            ignoreAnnea,
          })
        )
      );

      const mergedEvents = responses
        .flatMap((response, index) => {
          if (response.status === 'fulfilled') {
            return response.value.events || [];
          }
          console.warn('[AnalyticsAixm] Failed to load slug', selectedAppSlugs[index], response.reason);
          return [];
        })
        .sort((a, b) => {
          const timeA = new Date(a.occurredAt || a.createdAt || 0).getTime();
          const timeB = new Date(b.occurredAt || b.createdAt || 0).getTime();
          return timeB - timeA;
        });

      setEvents(mergedEvents);
      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      console.error('[AnalyticsAixm] Failed to load events', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const analytics = useMemo(() => analyzeEvents(events), [events]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>AIXM Viewer Analytics</Title>
          <Text type="secondary">
            Last updated {lastUpdated || '-'}
          </Text>
        </div>
        <Space>
          <Radio.Group
            value={viewerType}
            onChange={(event) => setViewerType(event.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: 'AIXM Viewer Light', value: 'light' },
              { label: 'AIXM Viewer Pro', value: 'pro' },
            ]}
          />
          <Text type="secondary">Auto refresh</Text>
          <Switch checked={autoRefresh} onChange={setAutoRefresh} />
          <Button onClick={() => void loadEvents()} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      <AnalyticsFilters
        ignoreAnnea={ignoreAnnea}
        organizationId={selectedOrgId}
        userId={selectedUserId}
        onIgnoreAnneaChange={setIgnoreAnnea}
        onOrganizationChange={(value) => {
          setSelectedOrgId(value);
          setSelectedUserId(null);
        }}
        onUserChange={setSelectedUserId}
      />

      {loading ? (
        <div>Loading analytics data...</div>
      ) : !analytics ? (
        <div>No analytics data available yet.</div>
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Text type="secondary">Total Opens</Text>
                <Title level={2} style={{ margin: 0 }}>{analytics.total}</Title>
                <Text type="secondary">Dataset opens recorded</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Text type="secondary">Dataset Size</Text>
                <Title level={2} style={{ margin: 0 }}>{formatFileSize(analytics.avgSize)}</Title>
                <Text type="secondary">
                  Avg | Max: {formatFileSize(analytics.maxSize)} | Total: {formatFileSize(analytics.totalSize)}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Text type="secondary">Indexing Time</Text>
                <Title level={2} style={{ margin: 0 }}>{analytics.avgDuration.toFixed(2)}s</Title>
                <Text type="secondary">
                  Avg | Max: {analytics.maxDuration.toFixed(2)}s | Total: {analytics.totalDuration.toFixed(2)}s
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Text type="secondary">Features</Text>
                <Title level={2} style={{ margin: 0 }}>{Math.round(analytics.avgFeatures).toLocaleString()}</Title>
                <Text type="secondary">
                  Avg | Max: {analytics.maxFeatures.toLocaleString()} | Total: {analytics.totalFeatures.toLocaleString()}
                </Text>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12} lg={8}>
              <BarList title="AIXM Versions" items={analytics.versions} />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <BarList title="Operating Systems" items={analytics.os} />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <BarList title="Browsers" items={analytics.browsers} />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <BarList title="Datasets by Feature Count" items={analytics.featureCountBands} />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <BarList title="Datasets by Indexing Duration" items={analytics.durationBands} />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <BarList title="Datasets by Size" items={analytics.sizeBands} />
            </Col>
            <Col xs={24}>
              <ColumnChart title="Datasets per Day (Last 30 Days)" items={analytics.daily} />
            </Col>
            {/*<Col xs={24} md={12} lg={12}>
              <BarList title="Top Files" items={analytics.files} />
            </Col>*/}
          </Row>

          <Card>
            <Title level={4}>Recent Dataset Opens</Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {analytics.recent.map((event) => {
                const params = (event.specific || {}) as Record<string, any>;
                return (
                  <div
                    key={event.id}
                    style={{
                      padding: 12,
                      background: '#fafafa',
                      borderRadius: 6,
                      borderLeft: '4px solid #f46524',
                    }}
                  >
                    <Text type="secondary">{formatTimestamp(event.occurredAt || event.createdAt)}</Text>
                    <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                      <div><strong>File:</strong> {params.file_name || 'N/A'}</div>
                      <div><strong>Size:</strong> {formatFileSize(params.dataset_size_kb || 0)}</div>
                      <div><strong>Features:</strong> {(params.total_features || 0).toLocaleString()}</div>
                      <div><strong>AIXM:</strong> {params.aixm_version || 'N/A'}</div>
                      <div><strong>Duration:</strong> {(params.indexing_duration_seconds || 0).toFixed(2)}s</div>
                      <div><strong>OS:</strong> {event.os || params.os || 'N/A'}</div>
                      <div><strong>Browser:</strong> {event.browser || params.browser || 'N/A'}</div>
                      <div><strong>CPU:</strong> {params.cpu_cores || 'N/A'} cores</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
